import { generateUUID } from '../utils/uuid'
import { ImageOutput, SourceCitation } from '../services/api/chat'

export type MessageStatus = 'loading' | 'complete' | 'error'
export interface Media {
  previewUrl?: string
  fileName?: string
  fileType?: string
  fileSize?: number
  file?: File
}

export interface MessageModel {
  id: string
  // Stable server-side UUID. Populated on assistant messages once the backend
  // returns a `message_id` via the chat SSE `response` event or history
  // hydration. Empty for user messages and for legacy history rows written
  // before the backend started issuing IDs. Addresses per-message endpoints
  // like /v1/chat/messages/{id}/clip.
  serverId?: string
  question: string
  type: 'sent' | 'received'
  time: Date
  media?: Media
  userId?: string // Made userId optional
  error?: boolean
  status?: MessageStatus
  editedFromId?: string
  images?: ImageOutput[]
  followUps?: string[]
  sources?: SourceCitation[]
  thinkingStages?: string[]
  streamingText?: string

  formatForDisplay(): string
  toObject(): object
}

export class Message implements MessageModel {
  id: string
  serverId?: string
  question: string
  type: 'sent' | 'received'
  time: Date
  media?: Media
  userId?: string // Optional userId in the class
  error?: boolean
  status?: MessageStatus
  editedFromId?: string
  images?: ImageOutput[]
  followUps?: string[]
  sources?: SourceCitation[]
  thinkingStages?: string[]
  streamingText?: string

  constructor(
    question: string,
    type: 'sent' | 'received',
    userId?: string,
    media?: Media,
    error?: boolean,
    status: MessageStatus = 'complete',
    editedFromId?: string,
    time?: Date,
    images?: ImageOutput[],
    followUps?: string[],
    sources?: SourceCitation[]
  ) {
    this.id = generateUUID()
    this.question = question
    this.type = type
    this.time = time ?? new Date()
    this.userId = userId
    this.media = media
    this.error = error
    this.status = status
    this.editedFromId = editedFromId
    this.images = images
    this.followUps = followUps
    this.sources = sources
  }

  formatForDisplay(): string {
    return `${this.time}: ${this.question}`
  }

  toObject(): object {
    return {
      id: this.id,
      text: this.question,
      type: this.type,
      time: this.time,
      media: this.media,
      userId: this.userId, // Include userId if it exists
      error: this.error,
      status: this.status,
      editedFromId: this.editedFromId,
      images: this.images,
      followUps: this.followUps,
      sources: this.sources,
    }
  }
}
